import React from "react";
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  Radio,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { QuestionnaireItemType } from "../../_helpers";
import { useTranslation } from "react-i18next";

const QuestionnaireQuestionOptions = ({
  question,
  sectionIndex,
  questionIndex,
  updateQuestion,
}) => {
  const theme = useTheme();
  const { t } = useTranslation()
  const options = Array.isArray(question.options) ? question.options : [];

  const addOption = () => {
    const newOption = {
      option: "",
      order: options.length,
    };
    updateQuestion(sectionIndex, questionIndex, {
      options: [...options, newOption],
    });
  };

  const deleteOption = (index) => {
    const newOptions = options
      .filter((_, i) => i !== index)
      .map((opt, i) => ({ ...opt, order: i }));

    updateQuestion(sectionIndex, questionIndex, { options: newOptions });
  };

  return (
    <>
      {[QuestionnaireItemType.Radio, QuestionnaireItemType.Checkbox].includes(
        question.type
      ) && (
          <Box sx={{ mt: 2 }}>
            {options.map((opt, i) => (
              <Stack key={i} direction="row" alignItems="center" spacing={1}>
                {question.type === QuestionnaireItemType.Radio ? (
                  <Radio disabled />
                ) : (
                  <Checkbox disabled />
                )}
                <TextField
                  variant="standard"
                  placeholder={t("questionnaire.form.optionWithNo", { number: i + 1 })}
                  value={opt.option || ""}
                  onChange={(e) => {
                    const newOpts = options.map((o, idx) =>
                      idx === i ? { ...o, option: e.target.value } : o
                    );
                    updateQuestion(sectionIndex, questionIndex, {
                      options: newOpts,
                    });
                  }}
                  sx={{ flex: 1 }}
                />
                <IconButton
                  size="small"
                  onClick={() => deleteOption(i)}
                  disabled={options.length === 1}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Stack>
            ))}

            <Box sx={{ mt: 1, ml: 4, display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                onClick={addOption}
                color="primary"
                variant="caption"
                sx={{ cursor: "pointer" }}
              >
                {t('questionnaire.form.addOption')}
              </Typography>
            </Box>
          </Box>
        )}

      {question.type === QuestionnaireItemType.ShortText && (
        <Box sx={{ mt: 2 }}>
          <TextField fullWidth variant="standard" placeholder={t('questionnaire.form.shortAnswerText')} disabled />
        </Box>
      )}

      {question.type === QuestionnaireItemType.Paragraph && (
        <Box sx={{ mt: 2 }}>
          <TextField
            fullWidth
            multiline
            minRows={3}
            variant="standard"
            placeholder={t('questionnaire.form.longAnswerText')}
            disabled
          />
        </Box>
      )}

      {question.type === QuestionnaireItemType.Dropdown && (
        <Box sx={{ mt: 2 }}>
          {options.map((opt, i) => (
            <Stack key={i} direction="row" alignItems="center" spacing={1}>
              <Typography sx={{ width: 20 }}>{i + 1}.</Typography>
              <TextField
                variant="standard"
                value={opt.option || ""}
                placeholder={t("questionnaire.form.optionWithNo", { number: i + 1 })}
                onChange={(e) => {
                  const newOpts = options.map((o, idx) =>
                    idx === i ? { ...o, option: e.target.value } : o
                  );
                  updateQuestion(sectionIndex, questionIndex, {
                    options: newOpts,
                  });
                }}
                sx={{ flex: 1 }}
              />
              <IconButton
                size="small"
                disabled={options.length === 1}
                onClick={() => deleteOption(i)}
              >
                <Close fontSize="small" />
              </IconButton>
            </Stack>
          ))}

          <Box sx={{ mt: 1, ml: 4 }}>
            <Button sx={{ textTransform: "none" }} onClick={addOption}>
              {t('questionnaire.form.addOption')}
            </Button>
          </Box>
        </Box>
      )}

      {question.type === QuestionnaireItemType.Date && (
        <TextField
          type="date"
          fullWidth
          sx={{ mt: 2 }}
          disabled
          slotProps={{
            inputLabel: {
              shrink: true,
            }
          }}
        />
      )}

      {question.type === QuestionnaireItemType.Time && (
        <TextField
          type="time"
          fullWidth
          sx={{ mt: 2 }}
          disabled
          slotProps={{
            inputLabel: {
              shrink: true,
            }
          }}
        />
      )}

      {question.type === QuestionnaireItemType.Signature && (
        <>
        <Box >
          <TextField
            fullWidth
            variant="standard"
            placeholder={t("general.signatoryName")}
            disabled
          />
        </Box>
        <Box
          sx={{
            mt: 2,
            height: 80,
            width: 150,
            border: `1px dashed ${theme.palette.divider}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {t('general.sign')}
        </Box>
        </>
      )}
    </>
  );
};

export default QuestionnaireQuestionOptions;
